import { action } from 'satcheljs';
import { actionWithDatapoint } from 'owa-analytics-actions';
import type { AssetId, ProjectId } from 'owa-timestream-types';
import getDefaultPanAndZoom from './selectors/getDefaultPanAndZoom';
import getNextZoomLevel from './selectors/getNextZoomLevel';
import getPersistedOrDefaultPanAndZoom from './selectors/getPersistedOrDefaultPanAndZoom';
import getSelectedAssets from './selectors/getSelectedAssets';
import getStore from './store';
import getZoomValues from './selectors/getZoomValues';
import getZoomByWheelValues from './selectors/getZoomByWheelValues';

export const trackCanvasBounds = action(
    'trackCanvasBounds',
    ({ top, left, bottom, right, width, height }: DOMRectReadOnly) => ({
        top,
        left,
        bottom,
        right,
        width,
        height,
    })
);

// Pointer Tracker
export const pointerMove = action('pointerMove', (dx: number, dy: number) => ({ dx, dy }));
export const pointerUp = action(
    'pointerUp',
    (
        bucketId: AssetId | undefined,
        targetMinOrder: number | undefined,
        targetMaxOrder: number | undefined
    ) => ({
        bucketId,
        targetMinOrder,
        targetMaxOrder,
    })
);

// Keyboard Tracker
export const moveAssetArrow = action('moveAssetArrow', (dx: number, dy: number) => ({ dx, dy }));
export const finishArrowing = action('finishArrowing');

// Zoom Tools
export const expandZoomTools = action('OUTLOOKSPACES_EXPAND_ZOOM_TOOLS');
export const collapseZoomTools = action('OUTLOOKSPACES_COLLAPSE_ZOOM_TOOLS');
export const focusZoomTools = actionWithDatapoint(
    'OUTLOOKSPACES_FOCUS_ZOOM_TOOLS',
    () => ({}),
    () => ({ options: { isCore: true } })
);
export const blurZoomTools = actionWithDatapoint(
    'OUTLOOKSPACES_BLUR_ZOOM_TOOLS',
    () => ({}),
    () => ({ options: { isCore: true } })
);

// Zoom
function zoomDatapoint({ zoom }: { zoom: number }) {
    return { customData: { zoom_1: zoom }, options: { isCore: true } };
}

function zoomAction(name: string, delta: number) {
    return actionWithDatapoint(
        name,
        () => getZoomValues(getNextZoomLevel(getStore().zoom, delta)),
        zoomDatapoint
    );
}

export const zoomIn = zoomAction('OUTLOOKSPACES_ZOOM_IN', 1);
export const zoomOut = zoomAction('OUTLOOKSPACES_ZOOM_OUT', -1);
export const zoomByWheel = actionWithDatapoint(
    'OUTLOOKSPACES_ZOOM_BY_WHEEL',
    getZoomByWheelValues,
    zoomDatapoint
);

// Pan with pointer
export const startPanning = action('startPanning');
export const finishPanning = action('endPanning', (x: number, y: number) => ({ x, y }));

// Pan with keyboard
export const panLeft = action('panLeft');
export const panRight = action('panRight');
export const panUp = action('panUp');
export const panDown = action('panDown');

export const panByWheel = action(
    'OUTLOOKSPACES_PAN_BY_WHEEL',
    (deltaX: number, deltaY: number) => ({ deltaX, deltaY })
);

export const resetPanAndZoom = actionWithDatapoint(
    'OUTLOOKSPACES_RESET_PAN_AND_ZOOM',
    (projectId?: ProjectId) =>
        projectId
            ? {
                  ...getPersistedOrDefaultPanAndZoom(projectId, true /* initialLoad */),
                  initialLoad: true,
              }
            : { ...getDefaultPanAndZoom(), initialLoad: false },
    (_, projectId) => ({ customData: { initialLoad_1: !!projectId }, options: { isCore: true } })
);

// Asset selection
const withAssetId = (assetId: AssetId) => ({ assetId });
export const selectAsset = action('selectAsset', withAssetId);
export const toggleAssetSelection = action('toggleAssetSelection', withAssetId);
export const clearSelectedAssets = action('clearSelectedAssets');
export const deleteSelectedAssets = action('deleteSelectedAssets', () => ({
    selectedAssets: [...getSelectedAssets()],
}));

// Move asset with pointer
export const startDraggingAssets = action('startDraggingAssets');

// hover over bucket
export const pointerOverBucket = action('pointerOverBucket');
export const pointerOutBucket = action('pointerOutBucket');

// toolbox
export const setIsUsingToolbox = action('setIsUsingToolbox', (isUsingToolbox: boolean) => ({
    isUsingToolbox,
}));

// Toolbox project picker
export const showProjectsToolbox = action('showProjectPicker');
export const hideProjectsToolbox = action('hideProjectPicker');

// enable canvas lock
export const setEnableCanvasLock = action('setEnableCanvasLock', enableCanvasLock => ({
    enableCanvasLock,
}));
